/** The masked glass stays still as its opacity fades, revealing the hero film. */
export const INTRO_TIMING = Object.freeze({ hold: 1500, dissolve: 1500, mediaTimeout: 1500 });

export function initializeGlassIntro(app, initialRoute) {
  const { hero, layout } = app;
  const overlay = document.getElementById('page-overlay');
  const header = document.querySelector('header');
  const protectedContent = [
    document.getElementById('main-content'),
    document.querySelector('footer'),
  ];
  const timers = new Set();
  const cleanups = [];
  let finished = false;

  const later = (callback, delay) => {
    const timer = setTimeout(() => {
      timers.delete(timer);
      callback();
    }, delay);
    timers.add(timer);
    return timer;
  };

  function finish() {
    if (finished) return;
    finished = true;
    timers.forEach(clearTimeout);
    timers.clear();
    cleanups.splice(0).forEach((cleanup) => cleanup());
    document.documentElement.classList.remove('intro-scroll-lock');
    document.body.classList.remove('intro-active', 'intro-dissolving');
    document.body.classList.add('loaded', 'intro-complete');
    if (overlay) {
      overlay.hidden = true;
      overlay.style.display = 'none';
    }
    if (header) header.inert = false;
    protectedContent.forEach((element) => { if (element) element.inert = false; });
    hero.heroIntroActive = false;
    hero.proposalHeroReleased = true;
    hero.queueHeroMediaResume();
    layout.queueDotGridZoneUpdate();
  }

  hero.finishIntro = finish;
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const returningToContent = window.history?.state?.route === 'home' && window.history.state.scrollY > 0;
  if (initialRoute !== 'home' || returningToContent || reducedMotion || !overlay) {
    finish();
    return;
  }

  document.body.classList.add('intro-active');
  document.documentElement.classList.add('intro-scroll-lock');
  if (header) header.inert = true;
  protectedContent.forEach((element) => { if (element) element.inert = true; });
  hero.heroIntroActive = true;
  hero.proposalHeroReleased = true;
  hero.syncHeroMediaPlayback();
  hero.startHeroAuto();

  const escape = (event) => { if (event.key === 'Escape') finish(); };
  document.addEventListener('keydown', escape);
  window.addEventListener('pagehide', finish, { once: true });
  cleanups.push(() => {
    document.removeEventListener('keydown', escape);
    window.removeEventListener('pagehide', finish);
  });

  const firstVideo = hero.heroTrack?.children[hero.getHeroDefaultIndex()]?.querySelector('video');
  const mediaReady = new Promise((resolve) => {
    if (!firstVideo || firstVideo.readyState >= 2 || firstVideo.error) return resolve();
    let mediaResolved = false;
    const ready = () => {
      if (mediaResolved) return;
      mediaResolved = true;
      firstVideo.removeEventListener('loadeddata', ready);
      firstVideo.removeEventListener('error', ready);
      resolve();
    };
    firstVideo.addEventListener('loadeddata', ready, { once: true });
    firstVideo.addEventListener('error', ready, { once: true });
    cleanups.push(ready);
    later(ready, INTRO_TIMING.mediaTimeout);
    firstVideo.preload = 'auto';
    if (firstVideo.networkState === HTMLMediaElement.NETWORK_EMPTY) firstVideo.load();
  });

  // A slow video has a bounded wait; its poster stays visible through the cutout.
  Promise.all([mediaReady, new Promise((resolve) => later(resolve, INTRO_TIMING.hold))])
    .then(() => {
      if (finished) return;
      // Let the browser finish painting the fade before revealing the navigation.
      const onFadeEnd = event => { if (event.target === overlay && event.propertyName === 'opacity') finish(); };
      overlay.addEventListener('transitionend', onFadeEnd);
      cleanups.push(() => overlay.removeEventListener('transitionend', onFadeEnd));
      document.body.classList.add('intro-dissolving');
      later(finish, INTRO_TIMING.dissolve + 300); // Covers a cancelled or unavailable transition.
    })
    .catch(finish);
}
